import {IDefaultVOption, IVOptionSetEndpoint, IVOptionSetLocal, TVOptionSet} from '~/interfaces/vSelectInterfaces'
import {ModelType} from '~/enums/modelType'
import {IProcessFlowVariable, ProcessFlowVariableType} from '~/interfaces/processFlowInterfaces'
import {RouteHelper} from '~/helpers/routeHelper'
import {Route} from '~/enums/route'
import Form, {IForm} from '../../../formability/form_builder/models/form'
import {IReferencePathNodeSpecific} from '~/interfaces/referencePathInterfaces'
import {GenericType} from '~/interfaces/genericTypeInterfaces'
import assertNever from '../../../global/assetNever'
import ExternalConnection from '~/models/externalConnections/externalConnection'
import EndPoint from '../../../formability/organization_settings/models/connections/endPoint'
import _ from 'lodash'
import {TCertificate} from "../../../formability/certificateBuilder/models/certificate";

export default class ProcessFlowVariableDefinition {
  static get typeOptionSet(): IVOptionSetLocal<IDefaultVOption<ProcessFlowVariableType> & { icon?: string }> {
    return {
      iconKey: 'icon',
      options: [
        { label: 'Record', value: ModelType.ObjectModel, icon: ProcessFlowVariableDefinition.variableTypeIcon(ModelType.ObjectModel)},
        { label: 'Form submission', value: ModelType.Form, icon: ProcessFlowVariableDefinition.variableTypeIcon(ModelType.Form) },
        { label: 'Sub-object', value: ModelType.DynamicInput, icon: ProcessFlowVariableDefinition.variableTypeIcon(ModelType.DynamicInput) },
        { label: "API", value: ModelType.EndPoint, icon: ProcessFlowVariableDefinition.variableTypeIcon(ModelType.EndPoint) },
        { label: "Document dynamic inputs", value: ModelType.CertificateDynamicInput, icon: ProcessFlowVariableDefinition.variableTypeIcon(ModelType.CertificateDynamicInput) },
        { label: 'File', value: ModelType.File, icon: ProcessFlowVariableDefinition.variableTypeIcon(ModelType.File) },
        { label: 'Text', value: ModelType.Text, icon: ProcessFlowVariableDefinition.variableTypeIcon(ModelType.Text) },
        { label: 'Number', value: ModelType.Number, icon: ProcessFlowVariableDefinition.variableTypeIcon(ModelType.Number) },
        { label: 'Boolean', value: ModelType.Boolean, icon: ProcessFlowVariableDefinition.variableTypeIcon(ModelType.Boolean) },
      ],
    }
  }

  static referenceSelectionOptions(variable): IVOptionSetEndpoint | IVOptionSetLocal | IVOptionSetLocal[] | {} {
    let options: IVOptionSetEndpoint | IVOptionSetLocal | IVOptionSetLocal[];

    switch (variable.convertedWrappedVariableTypeFromNodes) {
      case ModelType.ObjectModel:
        options = ProcessFlowVariableDefinition.objectModelSearchOptions(variable.parent)
        break
      case ModelType.Form:
        options = ProcessFlowVariableDefinition.formSearchOptions(variable.parent)
        break
      case ModelType.FormElement:
      case ModelType.FormSection: // intentional fall-through
        if (variable.referenceScopeModel instanceof Form) {
          options = ProcessFlowVariableDefinition.formElementContainerSearchOptionSets(variable.referenceScopeModel)
        }
        break
      case ModelType.EndPoint:
        if (variable.referenceScopeModel instanceof ExternalConnection) {
          options = ProcessFlowVariableDefinition.endpointSearchOptions(variable.referenceScopeModel)
        }
        break
      case ModelType.CertificateDynamicInput:
        if (variable.referenceScopeRequired) {
          options = ProcessFlowVariableDefinition.certificateDynamicInputSearchOptions(variable.referenceScopeModel)
        } else {
          options = ProcessFlowVariableDefinition.certificateSearchOptions(variable.parent);
        }
        break;
      default:
        return {}
    }

    if (variable.referenceModel && !Array.isArray(options)) options.selectedOption = variable.referenceModel

    return options
  }

  static referenceScopeSelectionOptions(variable): TVOptionSet[] | {} {
    let options: TVOptionSet[]

    if (variable.wrappedVariableType === ModelType.DynamicInput) {
      options = [
        ProcessFlowVariableDefinition.formSearchOptions(variable.parent),
        ProcessFlowVariableDefinition.certificateSearchOptions(variable.parent),
        ProcessFlowVariableDefinition.apiSearchOptions()
      ]
    } else {
      switch (variable.reference_scope_type) {
        case ModelType.Form:
          options = [ProcessFlowVariableDefinition.formSearchOptions(variable.parent)]
          break
        case ModelType.API:
          options = [ProcessFlowVariableDefinition.apiSearchOptions()]
          break
        default:
          return {}
      }
    }

    options.forEach(option => {
      if (variable.referenceScopeModel && variable.reference_scope_type === option.optionType) {
        option.selectedOption = variable.referenceScopeModel;
      }

      return option
    });

    return options
  }

  static subAPISet(endpoint: EndPoint): IVOptionSetLocal[] {
    return this.endpointObjectSearchOptions(endpoint);
  }

  static placeholderBasedOnVariableType(variableType: IProcessFlowVariable['variable_type'], referenceScopeRequired?: boolean): string {
    switch (variableType) {
      case ModelType.ObjectModel:
        return "Search an object model...";
      case ModelType.Form:
        return "Search a form...";
      case ModelType.FormElement:
      case ModelType.FormSection:  // intentional fall-through
        return "Search a form field container";
      case ModelType.API:
        return "Search an API connection...";
      case ModelType.EndPoint:
        return "Search an endpoint";
      case ModelType.DynamicInput:
        return "Search forms, APIs, and documents...";
      case ModelType.CertificateDynamicInput:
        if (referenceScopeRequired) {
          return 'Search an object...'
        }
        return "Search a document...";
      default:
        return "Select one...";
    }
  }

  static referenceSelectionLabel(variableType: IProcessFlowVariable['variable_type'], referenceScopeRequired?: boolean): string {
    if (variableType === ModelType.EndPoint) {
      return "Select endpoint"
    } else if ([ModelType.FormElement, ModelType.FormSection].includes(variableType)) {
      return "Form field container";
    } else if (variableType === ModelType.CertificateDynamicInput && referenceScopeRequired) {
      return 'Select an object';
    } else if (variableType === ModelType.CertificateDynamicInput) {
      return 'Select Document';
    } else {
      return `${ProcessFlowVariableDefinition.typeOptionSet.options.find(x => x.value === variableType)?.label} type` || ''
    }
  }

  static referenceScopeSelectionLabel(variable: Pick<IProcessFlowVariable, 'reference_scope_type' | 'wrappedVariableType'>): string {
    if (variable.wrappedVariableType === ModelType.DynamicInput) {
      return "Source type"
    } else if (variable.reference_scope_type === ModelType.API) {
      return "Select API connection"
    } else {
      return `${ProcessFlowVariableDefinition.typeOptionSet.options.find(x => x.value === variable.reference_scope_type)?.label} type` || ''
    }
  }

  private static formSearchOptions(processFlow): IVOptionSetEndpoint {
    return {
      heading: 'Forms',
      valueKey: 'id',
      labelKey: 'title',
      optionType: ModelType.Form,
      endpointFilters: { per_page: 5 },
      endpointOptions: {
        where: {
          application_id: processFlow.application_id
        }
      },
      endpoint: RouteHelper.path({ route: Route.processFlowSearchForms }),
      rootKey: 'forms',
    }
  }

  private static formElementContainerSearchOptionSets(form: IForm): IVOptionSetLocal[] {
    if (!form) return [];

    return [
      {
        heading: 'Form Elements',
        optionType: ModelType.FormElement,
        labelKey: 'referenceNameOrDefault',
        valueKey: 'id',
        options: form.elementContainerElements,
        valueComparer: (id, element) => id === element.id,
      },
      {
        heading: 'Form Sections',
        optionType: ModelType.FormSection,
        labelKey: 'titleOrDefault',
        valueKey: 'id',
        options: form.formSections,
        valueComparer: (id, formSection) => id === formSection.id,
      }
    ];
  }

  private static objectModelSearchOptions(processFlow): IVOptionSetEndpoint {
    return {
      valueKey: 'id',
      labelKey: 'title',
      endpointFilters: { per_page: 5 },
      endpointOptions: {
        where: {
          application_id: processFlow.application_id
        }
      },
      endpoint: RouteHelper.path({ route: Route.processFlowSearchObjectModels }),
      rootKey: 'object_models',
    }
  }

  private static apiSearchOptions(): IVOptionSetLocal {
    return {
      heading: 'APIs',
      valueKey: 'id',
      labelKey: 'name',
      optionType: ModelType.API,
      options: ExternalConnection.repo.all().filter((c) => c._type === 'ExternalConnection::Api')
    }
  }

  private static endpointSearchOptions(connection: ExternalConnection): IVOptionSetLocal {
    return {
      valueKey: 'id',
      labelKey: 'name',
      options: connection.endPointsArray()
    }
  }

  private static endpointObjectSearchOptions(endpoint: EndPoint): IVOptionSetLocal[] {
    return [
      {
        heading: 'Request',
        valueKey: 'id',
        labelKey: 'name',
        options: endpoint ? endpoint.requestObjectArray : []
      }, 
      {
        heading: 'Response',
        valueKey: 'id',
        labelKey: 'name',
        options: endpoint ? endpoint.responseObjectArray: []
      }
    ]
  }

  private static certificateSearchOptions(processFlow): IVOptionSetEndpoint {
    return {
      heading: 'Documents',
      valueKey: 'id',
      labelKey: 'title',
      optionType: ModelType.Certificate,
      endpointFilters: { per_page: 5 },
      endpointOptions: {
        where: {
          application_id: processFlow.application_id
        }
      },
      endpoint: RouteHelper.path({ route: Route.processSearchCertificates }),
      rootKey: "certificates",
    };
  }

  private static certificateDynamicInputSearchOptions(certificate: TCertificate): IVOptionSetLocal {
    return {
      labelKey: 'name',
      valueKey: 'id',
      options: certificate ? certificate.certificateDynamicObjectInputs : [],
    }
  }

  /**
   * Do not match single vs collection since this is only matching shape of the data
   *
   * @param variable
   * @param node
   */
  static matchesNodeShape(variable: IProcessFlowVariable, node: IReferencePathNodeSpecific): boolean {
    // if there is a reference, then matching the id is all that is needed
    if (variable.isVariableDynamicInput()) {
      return _.last(variable.reference_path.nodes).reference_id === node.reference_id;
    } else if (variable.referenceRequired) {
      return variable.reference_id === node.reference_id
    } else {
      return variable.genericType === node.service.referenceGenericModelType
    }
  }

  static genericType(variable: IProcessFlowVariable): GenericType {
    switch (variable.variable_type) {
      case ModelType.Form: // intentional fall-through
      case ModelType.FormElement: // intentional fall-through
      case ModelType.FormSection: // intentional fall-through
      case ModelType.EndPoint:
      case ModelType.API:
      case ModelType.DynamicInput:
      case ModelType.CertificateDynamicInput: // intentional fall-through
      case ModelType.ObjectModel:
        return ModelType.Object
      case ModelType.File:
        return ModelType.File
      case ModelType.Text:
        return ModelType.Text
      case ModelType.Number:
        return ModelType.Number
      case ModelType.Boolean:
        return ModelType.Boolean
      default:
        assertNever(variable.variable_type)
    }
  }

  static variableTypeIcon(variableType: ModelType): string {
    switch (variableType) {
      case ModelType.Form:
        return 'form'
      case ModelType.ObjectModel:
        return 'object-model'
      case ModelType.File:
        return 'file'
      case ModelType.FormElement:
      case ModelType.FormSection: // intentional fall-through
        return "stack"
      case ModelType.Text:
        return "text"
      case ModelType.Number:
        return "numbers"
      case ModelType.Boolean:
        return "toggle"
      case ModelType.EndPoint:
        return "api"
      case ModelType.DynamicInput:
        return 'box'
      case ModelType.CertificateDynamicInput:
        return 'document-dynamic-input'
      default:
        return ""
    }
  }
}
